import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="hero-carousel"
// Crossfades hand-written slides, auto-advancing every `interval` ms with a
// segmented progress bar per slide. Clicking an indicator jumps to that slide;
// hovering/focusing the carousel pauses it.
export default class extends Controller {
	static targets = ["slide", "indicator", "bar"];
	static values = {
		interval: { type: Number, default: 10000 },
		index: { type: Number, default: 0 },
	};

	connect() {
		this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		this.elapsed = 0;
		this.active = this.slideTargets.length > 1 && !this.reducedMotion;
		this.render();
		this.schedule();
	}

	disconnect() {
		this.stop();
	}

	// --- navigation ---

	go(event) {
		this.select(Number(event.currentTarget.dataset.index));
	}

	next() {
		this.select((this.indexValue + 1) % this.slideTargets.length);
	}

	select(index) {
		this.indexValue = index; // triggers indexValueChanged -> render
		this.elapsed = 0;
		this.schedule(); // restart the timer for the new slide
	}

	indexValueChanged() {
		this.render();
	}

	render() {
		this.slideTargets.forEach((slide, i) => {
			const current = i === this.indexValue;
			slide.classList.toggle("opacity-100", current);
			slide.classList.toggle("opacity-0", !current);
			slide.classList.toggle("pointer-events-none", !current);
			slide.setAttribute("aria-hidden", String(!current));
		});
		this.indicatorTargets.forEach((indicator, i) => {
			indicator.setAttribute("aria-current", i === this.indexValue ? "true" : "false");
		});
		this.updateBars();
	}

	// Past slides full, future empty, active one tracks elapsed time.
	updateBars() {
		const ratio = Math.min(this.elapsed / this.intervalValue, 1);
		this.barTargets.forEach((bar, i) => {
			bar.style.width = i < this.indexValue ? "100%" : i > this.indexValue ? "0%" : `${ratio * 100}%`;
		});
	}

	// --- pause / resume on hover & focus ---

	pause() {
		this.active = false;
		this.stop();
	}

	resume() {
		if (this.slideTargets.length <= 1 || this.reducedMotion) return;
		this.active = true;
		this.schedule();
	}

	// --- timer (requestAnimationFrame so pause/resume is exact) ---

	schedule() {
		if (!this.active || this.frame) return;
		this.lastTs = null;
		this.frame = requestAnimationFrame(this.tick);
	}

	stop() {
		if (!this.frame) return;
		cancelAnimationFrame(this.frame);
		this.frame = null;
	}

	tick = (ts) => {
		this.frame = null;
		if (this.lastTs != null) this.elapsed += ts - this.lastTs;
		this.lastTs = ts;

		this.updateBars();

		if (this.elapsed >= this.intervalValue) {
			this.next();
			return;
		}
		this.frame = requestAnimationFrame(this.tick);
	};
}
